import React, { useState, useRef,useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FiSave, FiX, FiUpload } from 'react-icons/fi';
import { BarLoader } from 'react-spinners';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const CreateDoc = () => {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [file, setFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [charCount, setCharCount] = useState(0);
  const [descriptionError, setDescriptionError] = useState(false);
  const quillRef = useRef(null);

  const [documentData, setDocumentData] = useState({
    title: '',
    description: '',
    type: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setDocumentData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleDescriptionChange = (value) => {
    const plainText = value.replace(/<[^>]*>/g, '');
    setCharCount(plainText.length);
    
    if (plainText.length > 1200) {
      setDescriptionError(true);
    } else {
      setDescriptionError(false);
    }

    setDocumentData(prev => ({
      ...prev,
      description: value
    }));
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      
      if (selectedFile.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = () => {
          setFilePreview(reader.result);
        };
        reader.readAsDataURL(selectedFile);
      } else {
        setFilePreview(null);
      }
    }
  };

  const removeFile = () => {
    setFile(null);
    setFilePreview(null);
    document.getElementById('file-upload').value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!file) {
      toast.error('Please upload a document file');
      return;
    }

    if (descriptionError) {
      toast.error('Description must be 1200 characters or less');
      return;
    }

    setSubmitting(true);

    try {
      const formData = new FormData();
      formData.append('title', documentData.title);
      formData.append('description', documentData.description);
      formData.append('type', documentData.type);
      formData.append('file', file);

      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/doc/docp`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      toast.success('Document created successfully');
      navigate('/admin/doc');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create document');
      setSubmitting(false);
    }
  };

  const quillModules = {
    toolbar: [
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'header': [1, 2, 3, false] }],
      [{ 'align': [] }],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      [{ 'indent': '-1' }, { 'indent': '+1' }],
      ['link'],
      ['clean']
    ],
  };

  const quillFormats = [
    'header', 'bold', 'italic', 'underline', 'strike', 
    'align', 'list', 'indent', 'link'
  ];

  // Focus editor when component mounts
  useEffect(() => {
    if (quillRef.current) {
      setTimeout(() => {
        const quill = quillRef.current.getEditor();
        quill.focus();
      }, 300);
    }
  }, []);

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-4xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-800">Create New Document</h1>
          <button
            onClick={() => navigate('/admin/doc')}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Back to Documents
          </button>
        </div>

        <div className="bg-white rounded-lg shadow overflow-hidden">
          <form onSubmit={handleSubmit}>
            <div className="p-6 space-y-6">
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                    Title *
                  </label>
                  <input
                    type="text"
                    id="title"
                    name="title"
                    value={documentData.title}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="type" className="block text-sm font-medium text-gray-700 mb-1">
                    Document Type *
                  </label>
                  <input
                    type="text"
                    id="type"
                    name="type"
                    value={documentData.type}
                    onChange={handleInputChange}
                    required
                    className="mt-1 block w-full rounded-md shadow-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 p-2 border"
                    placeholder="Enter document type (e.g., Report, Guideline)"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <div className="border border-gray-300 rounded-lg overflow-hidden">
                  <ReactQuill
                    ref={quillRef}
                    theme="snow"
                    value={documentData.description}
                    onChange={handleDescriptionChange}
                    modules={quillModules}
                    formats={quillFormats}
                    placeholder="Enter document description (max 1200 characters)..."
                    className="quill-editor"
                  />
                </div>
                <div className={`text-right text-sm mt-1 ${
                  charCount > 1200 ? 'text-red-500' : 'text-gray-500'
                }`}>
                  {charCount} / 1200 characters
                  {descriptionError && (
                    <span className="ml-2">(Maximum 1200 characters allowed)</span>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Document File *
                </label>
                <div className="mt-1 flex items-center">
                  <label
                    htmlFor="file-upload"
                    className="cursor-pointer inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <FiUpload className="mr-2" />
                    {file ? 'Change File' : 'Upload File'}
                  </label>
                  <input
                    id="file-upload"
                    name="file-upload"
                    type="file"
                    className="sr-only"
                    onChange={handleFileChange}
                    required
                  />
                  {file && (
                    <div className="ml-4 flex items-center">
                      <div className="flex items-center">
                        {filePreview ? (
                          <img 
                            src={filePreview} 
                            alt="Preview" 
                            className="h-8 w-8 object-cover rounded mr-2"
                          />
                        ) : (
                          <FiUpload className="text-gray-500 mr-2" />
                        )}
                        <span className="text-gray-700">{file.name}</span>
                      </div>
                      <button
                        type="button"
                        onClick={removeFile}
                        className="ml-2 text-red-600 hover:text-red-800 p-1"
                        title="Remove file"
                      >
                        <FiX />
                      </button>
                    </div>
                  )}
                </div>
                <p className="mt-1 text-sm text-gray-500">
                  Upload the document file (PDF, DOCX, etc.)
                </p>
              </div>

              {filePreview && (
                <div className="mt-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">File Preview:</p>
                  <div className="border rounded-lg p-3 bg-gray-50">
                    <img 
                      src={filePreview} 
                      alt="File preview" 
                      className="max-w-full h-auto max-h-48 mx-auto"
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="px-6 py-3 bg-gray-50 border-t border-gray-200 flex justify-end">
              <div className="space-x-3">
                <button
                  type="button"
                  onClick={() => navigate('/admin/documents')}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <FiX className="mr-2" />
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting || !file || descriptionError}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  {submitting ? (
                    <>
                      <BarLoader
                        color="#ffffff"
                        height={5}
                        width={20}
                        className="mr-2"
                      />
                      Creating...
                    </>
                  ) : (
                    <>
                      <FiSave className="mr-2" />
                      Create Document
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>

      <style jsx global>{`
        .quill-editor .ql-container {
          min-height: 150px;
          font-size: 16px;
          line-height: 1.6;
        }
        .quill-editor .ql-editor {
          min-height: 150px;
        }
        .quill-editor .ql-toolbar {
          border-radius: 4px 4px 0 0;
          border: 1px solid #e2e8f0;
          border-bottom: none;
        }
        .quill-editor .ql-container {
          border-radius: 0 0 4px 4px;
          border: 1px solid #e2e8f0;
        }
        .quill-editor .ql-container.ql-snow {
          border-top: none;
        }
      `}</style>
    </div>
  );
};

export default CreateDoc;